import clsx from 'clsx'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { XCircle } from 'phosphor-react'
import { Empty } from 'antd'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { ReactComponent as Eye } from '../assets/svgs/eye.svg'
import { ReactComponent as NotificationsSvg } from '../assets/svgs/notifications.svg'
import { api } from '../services/api'
import { getRelativeTimeFromNow } from '../utils/date'
import { getErrorMessage } from '../utils/getErrorMessage'
import { TOAST_ERROR_DEFAULT_OPTIONS } from '../constants/toast'
import { Spinner } from './Spinner'
import { type NotificationDomain } from '../types/domain/notification-domain'

type NotificationsProps = {
  isOpen: boolean
  onClose: () => void
  onToggleOpen: () => void
}

export const Notifications = ({
  isOpen,
  onClose,
  onToggleOpen,
}: NotificationsProps) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const cacheKey = 'notifications'
  const { data: notifications = [], isLoading } = useQuery(
    cacheKey,
    async () => {
      const response = await api.get('/notifications')
      return response.data
    }
  )
  const { mutate: markNotificationAsRead } = useMutation({
    async mutationFn (id: number) {
      await api.patch(`/notifications/${id}/mark-as-read`)
    },
  })

  const handleNotificationClick = (notification: NotificationDomain) => {
    navigate(`/painel/projects/${notification.projectId}`, {
      state: {
        taskId: notification.entityId,
      },
    })
    onClose()
  }

  const handleReadNotificationButtonClick = (
    notification: NotificationDomain
  ) => {
    markNotificationAsRead(notification.id, {
      onSuccess () {
        queryClient.invalidateQueries(cacheKey)
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={onToggleOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={clsx('relative', {
            'after:w-3 after:h-3 after:rounded-full after:bg-secondary-light after:block after:absolute after:top-[3px] after:right-0':
              notifications.length > 0,
          })}
        >
          <NotificationsSvg />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className="z-50 mt-4 mr-5 bg-white rounded-lg shadow-md w-96">
          <header className="flex items-center justify-between px-7 py-4 border-b-2 border-b-[#D9D9D9]">
            <strong className="text-lg font-medium">Notificações</strong>
            <button onClick={onClose}>
              <XCircle size={20} />
            </button>
          </header>

          <DropdownMenu.DropdownMenu>
            <div className="pt-6 pb-6 space-y-4 px-7">
              {isLoading ? (
                <div className="flex justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  {notifications?.length === 0 && (
                    <Empty description="Sem registro de notificações" />
                  )}

                  {notifications?.length > 0 && (
                    <div className="pr-4 space-y-4 overflow-y-auto max-h-64 scrollbar">
                      {notifications.map((notification: NotificationDomain) => (
                        <DropdownMenu.Item
                          className="outline-none [&:not(:last-child)]:pb-4 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-grey-500"
                          key={notification.id}
                        >
                          <div className="flex gap-6">
                            <button
                              className="text-left"
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                            >
                              {notification.type ===
                                'user_mentioned_comment' && (
                                <p>
                                  <strong>{notification.fromUser.name}</strong>{' '}
                                  marcou você em um comentário.
                                </p>
                              )}

                              {notification.type ===
                                'user_is_a_collaborator' && (
                                <p>
                                  <strong>{notification.fromUser.name}</strong>{' '}
                                  adicionou você como colaborador(a) de uma
                                  task.
                                </p>
                              )}

                              {notification.type === 'new_comment_added' && (
                                <p>
                                  <span className="inline-block mr-1 text-xs font-light">
                                    [colaborador]
                                  </span>
                                  <strong>{notification.fromUser.name}</strong>{' '}
                                  comentou em uma task que você é colaborador
                                </p>
                              )}

                              <time className="inline-block mt-2 text-sm text-primary-light">
                                {getRelativeTimeFromNow(notification.createdAt)}
                              </time>
                            </button>
                            <button
                              title="Marcar como lida"
                              className="inline-flex self-center"
                              onClick={() =>
                                handleReadNotificationButtonClick(notification)
                              }
                            >
                              <Eye />
                            </button>
                          </div>
                        </DropdownMenu.Item>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </DropdownMenu.DropdownMenu>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
